import { useMemo, useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  CheckCircle2,
  FileText,
  ChevronLeft,
  ChevronRight,
  Check,
  AlertTriangle,
} from 'lucide-react';
import { ReviewModal } from '../components/ReviewModal';
import { ModalConfermaSquadraTurno } from '../components/ModalConfermaSquadraTurno';
import { AiRiformulaModal, type AiMateriale } from '../components/AiRiformulaModal';
import { ReportWizardSteps, type MaterialeRow } from '../components/report/ReportWizardSteps';
import { apiGet, apiPost, apiUpload } from '../shared/apiClient';
import type { Chiamata, Squadra } from '../shared/types';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';
import { compressImages } from '../utils/imageCompress';
import { savePhoto, loadPhotos, clearPhotos } from '../utils/photoStore';
import { toRomeDateTimeInput, localToUtcIso, getTurnoDateStrRome } from '../shared/tz';

function toDatetimeLocal(d: Date): string {
  return toRomeDateTimeInput(d);
}

function getReportDraftKey(callId: string): string {
  return `roadhub_report_draft_${callId}`;
}

/**
 * Legge la bozza locale solo se appartiene al ciclo di lavorazione corrente.
 * La bozza memorizza quanti report aveva la chiamata quando è stata scritta: se nel frattempo
 * è stato inviato un report (chiamata "da terminare" ripresa dalle aperte) la bozza è obsoleta
 * e viene scartata, così la nuova compilazione parte da una base pulita.
 */
function readValidDraft(callId: string, reportsCount: number): any | null {
  try {
    const raw = localStorage.getItem(getReportDraftKey(callId));
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (!draft || typeof draft !== 'object' || (draft.reportsCount ?? 0) !== reportsCount) {
      localStorage.removeItem(getReportDraftKey(callId));
      return null;
    }
    return draft;
  } catch {
    return null;
  }
}

export function ReportPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useAuth();
  const isAiEnabled = !!user?.abilitaAiCorrettore;

  const adesso = useMemo(() => new Date(), []);
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(1);

  const [statoImpiantoArrivo, setStatoImpiantoArrivo] = useState<string>('Acceso a colori');
  const [statoImpiantoAltro, setStatoImpiantoAltro] = useState('');
  const [causaGuasto, setCausaGuasto] = useState('');
  const [azioniEseguite, setAzioniEseguite] = useState('');
  const [inizioLavoroAt, setInizioLavoroAt] = useState('');
  const [fineLavoroAt, setFineLavoroAt] = useState(() => toDatetimeLocal(adesso));
  const [esito, setEsito] = useState<string>('Risolto');
  const [statoDestinazione, setStatoDestinazione] = useState<'chiusa' | 'sospesa'>('chiusa');
  const inizializzatoRef = useRef(false);
  const fotoInizializzateRef = useRef(false);
  // true dopo l'invio riuscito: blocca l'auto-save che altrimenti ricreerebbe la bozza appena cancellata
  const reportInviatoRef = useRef(false);
  // N° di report già presenti quando la pagina si apre: identifica il ciclo di lavorazione della bozza
  const reportsCountRef = useRef(0);
  const [materiali, setMateriali] = useState<MaterialeRow[]>([{ descrizione: '', quantita: '0', um: 'N.' }]);
  const [fotoFiles, setFotoFiles] = useState<File[]>([]);
  const [compressioneFoto, setCompressioneFoto] = useState(false);
  const [showReview, setShowReview] = useState(false);

  // Assistente AI Riformulazione & Speech Recognition
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiData, setAiData] = useState<{
    testoOriginale: string;
    testoRiformulato: string;
    materiali: AiMateriale[];
    modifiche: string[];
  }>({
    testoOriginale: '',
    testoRiformulato: '',
    materiali: [],
    modifiche: [],
  });
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);

  // Dati squadra e verifica turno per collaboratori
  const { data: squadraMe } = useQuery<Squadra>({
    queryKey: ['squadra-me'],
    queryFn: async () => {
      const { data } = await apiGet<Squadra>('/squadre/me');
      return data;
    },
  });

  const [collaboratori, setCollaboratori] = useState<string>('');
  const [mezzoTarga, setMezzoTarga] = useState<string>('');
  const [autista, setAutista] = useState<string>('');
  const [showConfermaSquadraModal, setShowConfermaSquadraModal] = useState(false);
  const [modalGiaVerificato, setModalGiaVerificato] = useState(false);

  useEffect(() => {
    if (squadraMe) {
      if (squadraMe.collaboratori !== undefined) {
        setCollaboratori(squadraMe.collaboratori || '');
      }
      if (squadraMe.mezzoTarga !== undefined) {
        setMezzoTarga(squadraMe.mezzoTarga || '');
      }
      if (squadraMe.autista !== undefined) {
        setAutista(squadraMe.autista || '');
      }
      const turnoOggi = getTurnoDateStrRome();
      if (squadraMe.dotazioneTurno !== turnoOggi && !modalGiaVerificato) {
        setShowConfermaSquadraModal(true);
        setModalGiaVerificato(true);
      }
    }
  }, [squadraMe, modalGiaVerificato]);

  // Liste dinamiche configurate dall'amministratore
  const [statiImpiantoOpzioni, setStatiImpiantoOpzioni] = useState<string[]>([
    'Spento',
    'Acceso a colori',
    'Lampeggio',
    'Altro',
  ]);
  const [causeGuastoOpzioni, setCauseGuastoOpzioni] = useState<string[]>([
    'Guasto elettrico',
    'Guasto controller',
    'Guasto lanterna',
    'Guasto rilevatore',
    'Urto veicolare',
    'Allagamento',
    'Manutenzione programmata',
    'Altro',
  ]);
  const [esitiOpzioni, setEsitiOpzioni] = useState<string[]>([
    'Risolto',
    'Lasciato acceso a colori',
    'Lasciato acceso a lampeggio',
    'Lasciato spento',
    'Parzialmente risolto',
    'Non risolto',
  ]);

  useEffect(() => {
    apiGet<any>('/chiamate/liste-opzioni')
      .then((res) => {
        if (Array.isArray(res.data?.statiImpianto) && res.data.statiImpianto.length > 0) {
          setStatiImpiantoOpzioni(res.data.statiImpianto);
        }
        if (Array.isArray(res.data?.causeGuasto) && res.data.causeGuasto.length > 0) {
          setCauseGuastoOpzioni(res.data.causeGuasto);
        }
        if (Array.isArray(res.data?.esitiIntervento) && res.data.esitiIntervento.length > 0) {
          setEsitiOpzioni(res.data.esitiIntervento);
        }
      })
      .catch(() => {});
  }, []);

  // Sincronizza le foto su IndexedDB
  const syncPhotosToDB = async (callId: string, files: File[]) => {
    try {
      await clearPhotos(callId);
      await Promise.all(files.map((f) => savePhoto(callId, f)));
    } catch {
      /* best-effort */
    }
  };

  const rimuoviFoto = (index: number) => {
    setFotoFiles((prev) => {
      const next = prev.filter((_, i) => i !== index);
      if (id) syncPhotosToDB(id, next);
      return next;
    });
  };

  const aggiungiFoto = async (newFiles: File[]) => {
    if (newFiles.length === 0) return;
    setCompressioneFoto(true);
    try {
      const compressi = await compressImages(newFiles);
      setFotoFiles((prev) => {
        const next = [...prev, ...compressi].slice(0, 10);
        if (id) syncPhotosToDB(id, next);
        return next;
      });
    } catch {
      setFotoFiles((prev) => {
        const next = [...prev, ...newFiles].slice(0, 10);
        if (id) syncPhotosToDB(id, next);
        return next;
      });
    } finally {
      setCompressioneFoto(false);
    }
  };

  const callQuery = useQuery({
    queryKey: ['chiamata', id],
    enabled: !!id,
    queryFn: async () => (await apiGet<Chiamata>(`/chiamate/${id}`)).data,
  });

  // Foto salvate localmente: ripristinate solo se la bozza appartiene al ciclo di lavorazione corrente
  // (stessa regola della bozza testuale), altrimenti vengono scartate.
  useEffect(() => {
    const chiamata = callQuery.data;
    if (!id || !chiamata || fotoInizializzateRef.current) return;
    fotoInizializzateRef.current = true;
    const isModificaReport = chiamata.stato === 'report_squadra';
    if (!isModificaReport && !readValidDraft(id, chiamata.reports?.length ?? 0)) {
      clearPhotos(id).catch(() => {});
      return;
    }
    loadPhotos(id)
      .then((files) => {
        if (files.length > 0) setFotoFiles(files.slice(0, 10));
      })
      .catch(() => {});
  }, [id, callQuery.data]);

  useEffect(() => {
    if (inizializzatoRef.current) return;
    if (callQuery.isLoading || !callQuery.data) return;

    const chiamata = callQuery.data;
    reportsCountRef.current = chiamata.reports?.length ?? 0;

    // Se la chiamata è già chiusa definitivamente, blocco totale
    if (chiamata.stato === 'chiusa') {
      toast({ type: 'error', message: 'Questa chiamata è già chiusa definitivamente e non è più modificabile.' });
      navigate(`/chiamata/${id}`, { replace: true });
      return;
    }

    // Se la chiamata è in 'report_squadra', la squadra può modificare il proprio report già inviato
    const isModificaReport = chiamata.stato === 'report_squadra';

    // Controllo bloccante per nuove compilazioni: la chiamata deve essere presa in carico (inLavorazione = true)
    if (!chiamata.inLavorazione && !isModificaReport) {
      toast({ type: 'error', message: 'Devi prima prendere in carico la chiamata per poter compilare il report.' });
      navigate(`/chiamata/${id}`, { replace: true });
      return;
    }

    // Se la squadra sta modificando il proprio report in attesa di revisione, pre-popoliamo i campi con l'ultimo inviato
    if (isModificaReport) {
      const repSquadra = (chiamata.reports || []).slice().reverse().find((r) => r.redattoDa === 'squadra');

      if (repSquadra) {
        if (repSquadra.statoImpiantoArrivo) setStatoImpiantoArrivo(repSquadra.statoImpiantoArrivo);
        if (repSquadra.statoImpiantoAltro) setStatoImpiantoAltro(repSquadra.statoImpiantoAltro);
        if (repSquadra.causaGuasto) setCausaGuasto(repSquadra.causaGuasto);
        if (repSquadra.azioniEseguite) setAzioniEseguite(repSquadra.azioniEseguite);
        if (repSquadra.inizioLavoroAt) setInizioLavoroAt(toDatetimeLocal(new Date(repSquadra.inizioLavoroAt)));
        if (repSquadra.fineLavoroAt) setFineLavoroAt(toDatetimeLocal(new Date(repSquadra.fineLavoroAt)));
        if (repSquadra.esito) setEsito(repSquadra.esito);

        // Pre-carica materiali
        if (repSquadra.materiali) {
          let parsedMats: any[] = [];
          if (typeof repSquadra.materiali === 'string') {
            try { parsedMats = JSON.parse(repSquadra.materiali); } catch {}
          } else if (Array.isArray(repSquadra.materiali)) {
            parsedMats = repSquadra.materiali;
          }
          if (parsedMats.length > 0) {
            setMateriali(
              parsedMats.map((m: any) => ({
                descrizione: m.nome || m.descrizione || '',
                quantita: String(m.quantita || '1').replace(/^[A-Za-z.\s]+/, ''),
                um: (m.quantita || '').toUpperCase().includes('MT') ? 'MT.' : 'N.'
              }))
            );
          }
        }
      }
    } else {
      // Inizializzazione standard per NUOVO report (es. nuova chiamata o presa in carico di chiamata 'da terminare')
      const tipo = chiamata.impianto?.tipo;
      if (tipo === 'IS') {
        setEsito('Lasciato acceso a colori');
        setStatoImpiantoArrivo('Acceso a colori');
        setCausaGuasto('');
      } else {
        setStatoImpiantoArrivo('Altro');
        setCausaGuasto('');
        setEsito('');
      }
      const now = new Date();
      setInizioLavoroAt(toDatetimeLocal(now));
      setFineLavoroAt(toDatetimeLocal(now));
      setMateriali([{ descrizione: '', quantita: '0', um: 'N.' }]);
      setAzioniEseguite('');

      // Ripristino eventuale bozza salvata su localStorage (es. dopo kill fotocamera Android o reload pagina).
      // Valida solo se appartiene al ciclo di lavorazione corrente (vedi readValidDraft).
      if (id) {
        try {
          const draft = readValidDraft(id, reportsCountRef.current);
          if (draft) {
            if (draft.step >= 1 && draft.step <= 5) setStep(draft.step);
            if (typeof draft.statoImpiantoArrivo === 'string') setStatoImpiantoArrivo(draft.statoImpiantoArrivo);
            if (typeof draft.statoImpiantoAltro === 'string') setStatoImpiantoAltro(draft.statoImpiantoAltro);
            if (typeof draft.causaGuasto === 'string') setCausaGuasto(draft.causaGuasto);
            if (typeof draft.azioniEseguite === 'string') setAzioniEseguite(draft.azioniEseguite);
            if (typeof draft.inizioLavoroAt === 'string' && draft.inizioLavoroAt) setInizioLavoroAt(draft.inizioLavoroAt);
            if (typeof draft.fineLavoroAt === 'string' && draft.fineLavoroAt) setFineLavoroAt(draft.fineLavoroAt);
            if (typeof draft.esito === 'string' && draft.esito) setEsito(draft.esito);
            if (draft.statoDestinazione === 'chiusa' || draft.statoDestinazione === 'sospesa') {
              setStatoDestinazione(draft.statoDestinazione);
            }
            if (Array.isArray(draft.materiali) && draft.materiali.length > 0) {
              setMateriali(draft.materiali);
            }
            const hasUserContent =
              Boolean(draft.azioniEseguite?.trim()) ||
              Boolean(draft.statoImpiantoAltro?.trim()) ||
              (draft.step && draft.step > 1) ||
              (Array.isArray(draft.materiali) && draft.materiali.some((m: any) => m?.descrizione?.trim()));
            if (hasUserContent) {
              toast({ type: 'info', message: 'Bozza locale ripristinata' });
            }
          }
        } catch {
          /* ignore corrupted localStorage */
        }
      }
    }

    inizializzatoRef.current = true;
  }, [callQuery.data, callQuery.isLoading, id, navigate, toast]);

  // Auto-save bozza su localStorage ad ogni modifica (solo per nuove compilazioni post-inizializzazione)
  useEffect(() => {
    if (!id || !inizializzatoRef.current || reportInviatoRef.current) return;
    const stato = callQuery.data?.stato;
    if (!stato || stato === 'chiusa' || stato === 'report_squadra') return;
    try {
      const payload = {
        step,
        statoImpiantoArrivo,
        statoImpiantoAltro,
        causaGuasto,
        azioniEseguite,
        inizioLavoroAt,
        fineLavoroAt,
        esito,
        statoDestinazione,
        materiali,
        reportsCount: reportsCountRef.current,
        updatedAt: Date.now(),
      };
      localStorage.setItem(getReportDraftKey(id), JSON.stringify(payload));
    } catch {
      /* best-effort storage */
    }
  }, [
    id,
    callQuery.data?.stato,
    step,
    statoImpiantoArrivo,
    statoImpiantoAltro,
    causaGuasto,
    azioniEseguite,
    inizioLavoroAt,
    fineLavoroAt,
    esito,
    statoDestinazione,
    materiali,
  ]);

  const materialiPuliti = useMemo(() => {
    return materiali
      .map((m) => {
        const nome = (m.descrizione || (m as any).nome || '').trim();
        const rawQ = (m.quantita || '').trim();
        const um = m.um || 'N.';
        const qUpper = rawQ.toUpperCase();
        const quantita = rawQ
          ? (qUpper.startsWith('N.') || qUpper.startsWith('MT.') || qUpper.startsWith('PZ') ||
             qUpper.endsWith('N.') || qUpper.endsWith('MT.') || qUpper.endsWith('PZ')
              ? (qUpper.endsWith('N.') ? `N.${rawQ.replace(/\s*N\.?$/i, '')}` : rawQ)
              : (um === 'N.' ? `N.${rawQ}` : `${um} ${rawQ}`))
          : (um === 'N.' ? 'N.1' : um);
        return { nome, descrizione: nome, quantita, um };
      })
      .filter((m) => m.nome !== '');
  }, [materiali]);

  const aggiornaMateriale = (index: number, field: keyof MaterialeRow, value: string) => {
    setMateriali((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const rimuoviMateriale = (index: number) => {
    setMateriali((prev) => {
      if (prev.length === 1) return [{ descrizione: '', quantita: '0', um: 'N.' }];
      return prev.filter((_, i) => i !== index);
    });
  };

  const c = callQuery.data;

  // Assistente AI e Dettatura Vocale
  const handleToggleSpeech = () => {
    const SpeechRec = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRec) {
      toast({ type: 'error', message: 'Dettatura vocale non supportata da questo browser. Usa il microfono della tastiera.' });
      return;
    }
    if (isListening) {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
      setIsListening(false);
      return;
    }

    try {
      const recognition = new SpeechRec();
      recognition.lang = 'it-IT';
      recognition.continuous = true;
      recognition.interimResults = false;

      recognition.onresult = (event: any) => {
        let finalTranscript = '';
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript + ' ';
          }
        }
        if (finalTranscript) {
          setAzioniEseguite((prev) => (prev ? `${prev.trim()} ${finalTranscript.trim()}` : finalTranscript.trim()));
        }
      };

      recognition.onerror = () => {
        setIsListening(false);
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognition.start();
      recognitionRef.current = recognition;
      setIsListening(true);
      toast({ type: 'info', message: '🎙️ Microfono attivo: parla adesso per descrivere l\'intervento...' });
    } catch {
      setIsListening(false);
    }
  };

  const handleMiglioraConAi = async () => {
    if (!azioniEseguite.trim()) {
      toast({ type: 'error', message: 'Scrivi o detta prima una breve bozza dell\'intervento' });
      return;
    }
    setIsAiLoading(true);
    try {
      const res = await apiPost<any>(
        '/ai/riformula-report',
        {
          testo: azioniEseguite.trim(),
          contesto: {
            impiantoCodice: c?.impianto?.codice,
            impiantoNome: c?.impianto?.nome,
            causaGuasto,
            statoImpiantoArrivo,
          },
        },
        { timeout: 60000 }
      );

      if (res.data && res.data.ok) {
        setAiData({
          testoOriginale: azioniEseguite.trim(),
          testoRiformulato: res.data.testoRiformulato,
          materiali: res.data.materiali || [],
          modifiche: res.data.modifiche || [],
        });
        setAiModalOpen(true);
      } else {
        toast({ type: 'error', message: res.data?.errore || 'Errore elaborazione AI' });
      }
    } catch (err: any) {
      console.error('Errore chiamata AI:', err);
      toast({ type: 'error', message: err.response?.data?.errore || err.message || 'Assistente AI non disponibile' });
    } finally {
      setIsAiLoading(false);
    }
  };

  const handleApplicaRiformulazione = (nuovoTesto: string, materialiDaAggiungere: AiMateriale[]) => {
    setAzioniEseguite(nuovoTesto);
    if (materialiDaAggiungere && materialiDaAggiungere.length > 0) {
      setMateriali((prev) => {
        const filtered = prev.filter((m) => m.descrizione.trim() !== '' || (m.quantita && m.quantita !== '0'));
        const daAggiungere: MaterialeRow[] = materialiDaAggiungere.map((m) => ({
          descrizione: m.descrizione,
          quantita: String(m.quantita || '1'),
          um: (m.um === 'MT.' ? 'MT.' : 'N.') as 'N.' | 'MT.',
        }));
        return [...filtered, ...daAggiungere];
      });
      toast({ type: 'success', message: 'Testo riformulato e ricambi aggiunti al Passo 3!' });
    } else {
      toast({ type: 'success', message: 'Testo tecnico applicato al report!' });
    }
  };

  // Validazioni vincolanti per ogni passo del Wizard
  const validateStep1 = (): boolean => {
    const isIS = c?.impianto?.tipo === 'IS';
    if (isIS) {
      if (statoImpiantoArrivo?.toLowerCase() === 'altro' && !statoImpiantoAltro.trim()) {
        toast({ type: 'error', message: 'Specifica lo stato dell\'impianto all\'arrivo' });
        return false;
      }
      if (!causaGuasto.trim()) {
        toast({ type: 'error', message: 'Indica la causa del guasto' });
        return false;
      }
    } else {
      if (!statoImpiantoAltro.trim()) {
        toast({ type: 'error', message: 'Descrivi lo stato dell\'impianto all\'arrivo' });
        return false;
      }
      if (!causaGuasto.trim()) {
        toast({ type: 'error', message: 'Descrivi la causa del guasto' });
        return false;
      }
    }
    return true;
  };

  const errOrari = useMemo(() => {
    if (!inizioLavoroAt || !fineLavoroAt) return null;
    const dInizio = new Date(inizioLavoroAt).getTime();
    const dFine = new Date(fineLavoroAt).getTime();

    if (dInizio > dFine) {
      return {
        tipo: 'inversione',
        titolo: 'Attenzione: Inizio lavoro successivo alla Fine lavoro!',
        messaggio: 'L\'orario di inizio lavoro sul posto non può essere successivo all\'orario di fine lavoro.',
      };
    }

    if (c?.dataOra) {
      const aperturaMs = new Date(c.dataOra).getTime();
      const aperturaStr = new Date(c.dataOra).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

      if (dInizio < aperturaMs) {
        return {
          tipo: 'antecedente_inizio',
          titolo: 'Attenzione: Orario di Inizio Lavoro non valido!',
          messaggio: `L'orario di inizio lavoro sul posto non può essere antecedente alla data e ora di creazione della chiamata (${aperturaStr}).`,
        };
      }
      if (dFine < aperturaMs) {
        return {
          tipo: 'antecedente_fine',
          titolo: 'Attenzione: Orario di Fine Lavoro non valido!',
          messaggio: `L'orario di fine lavoro sul posto non può essere antecedente alla data e ora di creazione della chiamata (${aperturaStr}).`,
        };
      }
    }

    return null;
  }, [c, inizioLavoroAt, fineLavoroAt]);

  const validateStep2 = (): boolean => {
    if (!inizioLavoroAt) {
      toast({ type: 'error', message: 'Indica l\'orario di inizio lavoro sul posto' });
      return false;
    }
    if (!fineLavoroAt) {
      toast({ type: 'error', message: 'Indica l\'orario di fine lavoro sul posto' });
      return false;
    }
    if (errOrari) {
      toast({ type: 'error', message: errOrari.messaggio });
      return false;
    }
    if (!azioniEseguite.trim()) {
      toast({ type: 'error', message: 'Descrivi le azioni e gli interventi svolti' });
      return false;
    }
    return true;
  };

  const validateStep3 = (): boolean => {
    for (let i = 0; i < materiali.length; i++) {
      const row = materiali[i];
      if (row.quantita && row.quantita.trim() !== '0' && row.quantita.trim() !== '' && !row.descrizione.trim()) {
        toast({ type: 'error', message: `Inserisci la descrizione per il materiale riga ${i + 1}` });
        return false;
      }
    }
    return true;
  };

  const validateStep4 = (): boolean => {
    if (!esito) {
      toast({ type: 'error', message: 'Seleziona l\'esito tecnico dell\'intervento' });
      return false;
    }
    return true;
  };

  const validateStep5 = (): boolean => {
    if (!statoDestinazione) {
      toast({ type: 'error', message: 'Seleziona lo stato finale della chiamata' });
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    if (step === 1) {
      if (validateStep1()) setStep(2);
    } else if (step === 2) {
      if (validateStep2()) setStep(3);
    } else if (step === 3) {
      if (validateStep3()) setStep(4);
    } else if (step === 4) {
      if (validateStep4()) setStep(5);
    } else if (step === 5) {
      if (validateStep1() && validateStep2() && validateStep3() && validateStep4() && validateStep5()) {
        setShowReview(true);
      }
    }
  };

  const handlePrevStep = () => {
    if (step > 1) {
      setStep((step - 1) as 1 | 2 | 3 | 4 | 5);
    }
  };

  const handleGoToStep = (targetStep: 1 | 2 | 3 | 4 | 5) => {
    if (targetStep < step) {
      setStep(targetStep);
      return;
    }
    if (targetStep >= 2 && !validateStep1()) return;
    if (targetStep >= 3 && !validateStep2()) return;
    if (targetStep >= 4 && !validateStep3()) return;
    if (targetStep >= 5 && !validateStep4()) return;
    setStep(targetStep);
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('ID chiamata mancante');
      if (!validateStep1() || !validateStep2() || !validateStep3() || !validateStep4() || !validateStep5()) {
        throw new Error('Dati non validi o mancanti');
      }

      const form = new FormData();
      form.append('statoImpiantoArrivo', statoImpiantoArrivo);
      if (statoImpiantoAltro) form.append('statoImpiantoAltro', statoImpiantoAltro);
      form.append('causaGuasto', causaGuasto);
      form.append('azioniEseguite', azioniEseguite);
      form.append('inizioLavoroAt', localToUtcIso(inizioLavoroAt));
      form.append('fineLavoroAt', localToUtcIso(fineLavoroAt));
      form.append('materiali', JSON.stringify(materialiPuliti));
      form.append('esito', esito);
      form.append('statoDestinazione', statoDestinazione);
      form.append('collaboratori', (collaboratori || '').trim());
      form.append('mezzoTarga', (mezzoTarga || '').trim());
      form.append('autista', (autista || '').trim());
      fotoFiles.forEach((file) => form.append('foto', file));
      return apiUpload(`/chiamate/${id}/report`, form);
    },
    onSuccess: async () => {
      reportInviatoRef.current = true;
      if (id) {
        try { localStorage.removeItem(getReportDraftKey(id)); } catch { /* best-effort */ }
        try { await clearPhotos(id); } catch { /* best-effort */ }
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['chiamata', id] }),
        queryClient.invalidateQueries({ queryKey: ['pending-calls'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['chiamate-aperte'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['chiamate-da-terminare'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['squadra-chiuse-oggi'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['report-giornalieri'], refetchType: 'all' }),
        queryClient.invalidateQueries({ queryKey: ['chiamate-giorno'], refetchType: 'all' }),
      ]);
      toast({ type: 'success', message: 'Report salvato con successo' });
      navigate('/', { replace: true });
    },
    onError: (err: any) => {
      const status = err?.response?.status;
      const data = err?.response?.data;
      const msg =
        (data && (data.errore || (typeof data === 'string' ? data : JSON.stringify(data)))) ||
        err?.message ||
        'Invio report fallito';
      toast({ type: 'error', message: `ERR ${status || '?'}: ${msg}` });
    },
  });

  const isBusy = saveMutation.isPending;
  const currentStatus = c ? c.stato : '—';

  if (callQuery.isLoading) {
    return (
      <div className="space-y-4">
        <div className="skeleton h-12 w-28" />
        <div className="skeleton h-64" />
      </div>
    );
  }

  if (!c) {
    return (
      <div className="card-section text-center p-8">
        <h3 className="font-bold text-slate-800 text-lg">Chiamata non trovata</h3>
        <button className="btn-secondary mt-4" onClick={() => navigate('/')}>
          Torna alla Dashboard
        </button>
      </div>
    );
  }

  const immutabile = c.stato === 'chiusa';

  if (immutabile) {
    return (
      <div className="space-y-4 animate-fade-in">
        <div className="flex items-center justify-between gap-3">
          <button className="btn-outline px-3.5 py-2 text-xs font-bold gap-1.5 shadow-2xs" type="button" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4" /> Indietro
          </button>
          <Link to={`/chiamata/${c.id}`} className="btn-secondary px-3 py-2 text-xs font-bold">
            Dettaglio
          </Link>
        </div>
        <section className="card-section space-y-3 text-center p-6">
          <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto" />
          <h2 className="page-title text-xl font-extrabold text-slate-900">Intervento già concluso</h2>
          <p className="text-xs sm:text-sm text-slate-600 max-w-md mx-auto">
            Questa chiamata è già in stato <strong>{currentStatus}</strong>. Non è possibile inviare un nuovo report.
          </p>
          <Link to={`/chiamata/${c.id}`} className="btn-primary inline-flex mt-2">
            Visualizza report salvati
          </Link>
        </section>
      </div>
    );
  }

  const stepsList = [
    { num: 1, label: 'Diagnosi', desc: 'Stato & Guasto' },
    { num: 2, label: 'Intervento', desc: 'Orari & Azioni' },
    { num: 3, label: 'Materiali', desc: 'Ricambi usati' },
    { num: 4, label: 'Esito Tecnico', desc: 'Risultato intervento' },
    { num: 5, label: 'Stato & Foto', desc: 'Foto & Destinazione' },
  ];

  return (
    <div className="space-y-4 animate-fade-in pb-16">
      {/* ── BARRA SUPERIORE ── */}
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          className="btn-outline px-3.5 py-2 text-xs font-bold gap-1.5 shadow-2xs"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Dashboard</span>
        </button>
        <Link to={`/chiamata/${c.id}`} className="btn-outline px-3 py-1.5 text-xs font-bold text-slate-600">
          Scheda Impianto
        </Link>
      </div>

      {/* ── INTESTAZIONE REPORT ── */}
      <section className="card-section p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center font-extrabold text-sm shrink-0 ${
              callQuery.data?.stato === 'report_squadra' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'
            }`}>
              {callQuery.data?.stato === 'report_squadra' ? '✏️' : '📋'}
            </div>
            <div>
              <div className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">
                {callQuery.data?.stato === 'report_squadra' ? 'Modifica Report Esistente' : 'Compilazione Report'}
              </div>
              <h2 className="font-extrabold text-base sm:text-lg text-slate-900 leading-snug">
                {c.impianto?.codice} · {c.impianto?.nome}
              </h2>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {callQuery.data?.stato === 'report_squadra' && (
              <span className="badge-amber text-xs font-bold shrink-0">
                In revisione
              </span>
            )}
            <span className="badge-verde text-xs font-bold shrink-0">
              Passo {step}/5
            </span>
          </div>
        </div>
      </section>

      {/* ── STEPPER NAVIGATORE A 5 PASSI ── */}
      <section className="card-section p-3 sm:p-4">
        <div className="grid grid-cols-5 gap-1 sm:gap-2">
          {stepsList.map((st) => {
            const isActive = step === st.num;
            const isCompleted = step > st.num;
            return (
              <button
                key={st.num}
                type="button"
                onClick={() => handleGoToStep(st.num as 1 | 2 | 3 | 4 | 5)}
                className={`flex flex-col items-center justify-center p-2 rounded-2xl border transition-all text-center ${
                  isActive
                    ? 'border-emerald-500 bg-emerald-50/80 text-emerald-950 shadow-xs ring-2 ring-emerald-500/20'
                    : isCompleted
                    ? 'border-slate-200 bg-slate-50 text-emerald-700 font-bold'
                    : 'border-slate-100 bg-white text-slate-400 hover:bg-slate-50'
                }`}
              >
                <div
                  className={`w-6 h-6 rounded-full flex items-center justify-center font-extrabold text-xs mb-1 ${
                    isActive
                      ? 'bg-emerald-600 text-white'
                      : isCompleted
                      ? 'bg-emerald-100 text-emerald-800'
                      : 'bg-slate-100 text-slate-500'
                  }`}
                >
                  {isCompleted ? <Check className="w-3.5 h-3.5" /> : st.num}
                </div>
                <span className="text-[11px] sm:text-xs font-extrabold line-clamp-1">
                  {st.label}
                </span>
                <span className="text-[9px] text-slate-400 hidden sm:block">
                  {st.desc}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {/* ── SEGNALAZIONE INTERVENTI PRECEDENTI SU QUESTA CHIAMATA (DA TERMINARE) ── */}
      {callQuery.data?.stato !== 'report_squadra' && (callQuery.data?.reports || []).filter((r) => r.redattoDa !== 'operatore').length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4 space-y-2.5">
          <div className="flex items-center gap-2 text-amber-900 font-extrabold text-xs">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
            <span>Interventi precedenti registrati per questa chiamata:</span>
          </div>
          <div className="space-y-2 text-xs text-amber-950">
            {(callQuery.data?.reports || []).filter((r) => r.redattoDa !== 'operatore').map((rep) => (
              <div key={rep.id} className="rounded-xl bg-white/80 border border-amber-100 p-2.5 space-y-1">
                <div className="flex items-center justify-between font-bold text-amber-900">
                  <span>{rep.squadraNome || 'Squadra precedente'}</span>
                  <span className="text-[11px] text-amber-700 font-medium">
                    {rep.fineLavoroAt ? new Date(rep.fineLavoroAt).toLocaleString('it-IT') : ''}
                  </span>
                </div>
                {rep.azioniEseguite && (
                  <div><span className="font-semibold text-slate-600">Azioni:</span> {rep.azioniEseguite}</div>
                )}
                {rep.esito && (
                  <div><span className="font-semibold text-slate-600">Esito:</span> {rep.esito}</div>
                )}
              </div>
            ))}
          </div>
          <p className="text-[11px] text-amber-800 font-medium italic">
            Stai compilando il nuovo report indipendente per il tuo turno di lavoro. Gli orari e i materiali sopra riportati appartengono all'intervento precedente e non vengono duplicati.
          </p>
        </div>
      )}


      {/* ── CORPO WIZARD PASSO-PASSO ── */}
      <ReportWizardSteps
        step={step}
        c={c}
        statoImpiantoArrivo={statoImpiantoArrivo}
        setStatoImpiantoArrivo={setStatoImpiantoArrivo}
        statoImpiantoAltro={statoImpiantoAltro}
        setStatoImpiantoAltro={setStatoImpiantoAltro}
        statiImpiantoOpzioni={statiImpiantoOpzioni}
        causaGuasto={causaGuasto}
        setCausaGuasto={setCausaGuasto}
        causeGuastoOpzioni={causeGuastoOpzioni}
        inizioLavoroAt={inizioLavoroAt}
        setInizioLavoroAt={setInizioLavoroAt}
        fineLavoroAt={fineLavoroAt}
        setFineLavoroAt={setFineLavoroAt}
        errOrari={errOrari}
        isAiEnabled={isAiEnabled}
        isListening={isListening}
        handleToggleSpeech={handleToggleSpeech}
        isAiLoading={isAiLoading}
        handleMiglioraConAi={handleMiglioraConAi}
        azioniEseguite={azioniEseguite}
        setAzioniEseguite={setAzioniEseguite}
        materiali={materiali}
        setMateriali={setMateriali}
        aggiornaMateriale={aggiornaMateriale}
        rimuoviMateriale={rimuoviMateriale}
        esito={esito}
        setEsito={setEsito}
        esitiOpzioni={esitiOpzioni}
        fotoFiles={fotoFiles}
        rimuoviFoto={rimuoviFoto}
        aggiungiFoto={aggiungiFoto}
        compressioneFoto={compressioneFoto}
        statoDestinazione={statoDestinazione}
        setStatoDestinazione={setStatoDestinazione}
        nomeSquadra={user?.nome || squadraMe?.nome || 'Squadra'}
        collaboratori={collaboratori}
        mezzoTarga={mezzoTarga}
        autista={autista}
        onModificaCollaboratori={() => setShowConfermaSquadraModal(true)}
      />

      {/* ── BARRA INFERIORE NAVIGAZIONE WIZARD (FOOTER) ── */}
      <section className="card-section p-4 sm:p-5 shadow-sm bg-white border border-slate-200/90 rounded-2xl mt-4">
        <div className="flex items-center justify-between gap-3">
          <button
            type="button"
            className="btn-secondary py-3 px-4 sm:px-6 text-xs sm:text-sm font-bold gap-1.5 min-h-[46px]"
            onClick={handlePrevStep}
            disabled={step === 1 || isBusy}
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Precedente</span>
          </button>

          {step < 5 ? (
            <button
              type="button"
              className="btn-primary py-3 px-5 sm:px-7 text-xs sm:text-sm font-extrabold gap-1.5 shadow-md min-h-[46px]"
              onClick={handleNextStep}
              disabled={isBusy}
            >
              <span>Successivo</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              className="btn-primary py-3 px-5 sm:px-7 text-xs sm:text-sm font-extrabold gap-1.5 shadow-md bg-emerald-600 hover:bg-emerald-700 min-h-[46px]"
              onClick={handleNextStep}
              disabled={isBusy}
            >
              <FileText className="w-4 h-4" />
              <span>📋 Revisione e Invio</span>
            </button>
          )}
        </div>
      </section>

      {/* ── MODALE DI REVISIONE FINALE ── */}
      <ReviewModal
        isOpen={showReview}
        onClose={() => setShowReview(false)}
        onConfirm={() => {
          setShowReview(false);
          saveMutation.mutate();
        }}
        isBusy={isBusy}
        data={{
          squadraNome: user?.nome || squadraMe?.nome || 'Squadra',
          collaboratori,
          mezzoTarga,
          autista,
          impiantoCodice: c.impianto?.codice,
          impiantoNome: c.impianto?.nome,
          impiantoTipo: c.impianto?.tipo || undefined,
          statoImpiantoArrivo: statoImpiantoArrivo?.toLowerCase() === 'altro' && statoImpiantoAltro?.trim()
            ? `Altro (${statoImpiantoAltro.trim()})`
            : (statoImpiantoArrivo || statoImpiantoAltro || '—'),
          causaGuasto: causaGuasto || '—',
          azioniEseguite,
          inizioLavoroAt,
          fineLavoroAt,
          materiali: materialiPuliti,
          esitoLabel: esito || '—',
          statoDestinazione,
        }}
      />

      {/* ── MODALE CONFERMA SQUADRA DEL TURNO ── */}
      <ModalConfermaSquadraTurno
        isOpen={showConfermaSquadraModal}
        onClose={() => setShowConfermaSquadraModal(false)}
        squadraMe={squadraMe}
        motivo="inizio_report"
        onConfermato={(data) => {
          setCollaboratori(data.collaboratori || '');
          setMezzoTarga(data.mezzoTarga || '');
          setAutista(data.autista || '');
          setShowConfermaSquadraModal(false);
        }}
      />

      {isAiEnabled && (
        <AiRiformulaModal
          isOpen={aiModalOpen}
          onClose={() => setAiModalOpen(false)}
          testoOriginale={aiData.testoOriginale}
          testoRiformulato={aiData.testoRiformulato}
          materiali={aiData.materiali}
          modifiche={aiData.modifiche}
          onApplica={handleApplicaRiformulazione}
        />
      )}
    </div>
  );
}
